import type { ButtonProps, SxProps } from '@mui/material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { isNotNullOrUndefined } from '@vy/utils';
import React from 'react';
import EmptyState from '../../../assets/images/empty-state.svg';
import { NavLink } from 'react-router-dom';

type VyEmptyStateProps = ButtonProps & {
  title: string;
  description: string;
  ctaText?: string;
  to?: string;
  onCtaClick?: () => void;
  titleSx?: SxProps;
  descriptionSx?: SxProps;
  ctaSx?: SxProps;
  icon?: React.ReactNode;
  spacing?: number;
  gap?: number;
};

export const VyEmptyState: React.FC<VyEmptyStateProps> = ({
  title,
  description,
  ctaText,
  titleSx,
  descriptionSx,
  ctaSx,
  icon,
  onCtaClick,
  spacing = 0.5,
  gap = 0.5,
  ...props
}) => {
  const shouldNavigateOnCtaClick = isNotNullOrUndefined(props.to) && props.component === NavLink;
  const ctaExists = isNotNullOrUndefined(onCtaClick) || shouldNavigateOnCtaClick;

  return (
    <Stack
      spacing={spacing}
      sx={{
        gap: gap,
        alignItems: "center",
        justifyContent: 'center',
        height: '100%'
      }}>
      <Box
        sx={{
          position: "relative",
          width: 62,
          height: 71
        }}>
        {icon || (
          <img src={EmptyState} style={{ position: 'absolute', width: 62, height: 71, top: 0, left: 0 }} />
        )}
      </Box>
      <Typography
        variant="h5"
        sx={[{
          color: "text.primary",
          fontFamily: "Inter, sans-serif",
          fontWeight: "normal"
        }, ...(Array.isArray(titleSx) ? titleSx : [titleSx])]}>
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={[{
          color: "text.primary"
        }, ...(Array.isArray(descriptionSx) ? descriptionSx : [descriptionSx])]}>
        {description}
      </Typography>
      {isNotNullOrUndefined(ctaText) && ctaExists && (
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          {...props}
          sx={{
            borderRadius: 1,
            color: 'grey.600',
            borderColor: 'grey.400',
            textTransform: 'none',
            ...ctaSx,
          }}
          onClick={onCtaClick}
        >
          {ctaText}
        </Button>
      )}
    </Stack>
  );
};
